<script setup>
import { onMounted, ref } from 'vue'
import Ip from '@/api/Ip'
import User from '@/api/User'
import IpView from '@/views/Authenticated/IpView.vue'
import IpTableView from '@/components/Query/IpTableView.vue'
import { useDataSourcesStore } from '@product/stores/dataSources'

const dataSourcesStore = useDataSourcesStore()

const failed = ref(false)
const results = ref(null)
const running = ref(false)
const error_message = ref("")

const props = defineProps({
  input: {
    type: Array,
    default: () => []
  },
  type: {
    type: String,
    default: 'ip_table'
  },
  log: {
    type: Boolean,
    default: true
  }
})

onMounted(async () => {
  running.value = true

  try {
    if (props.type === 'ip') {
      // Request every queryable enrichment explicitly so the single-IP cards get
      // the slow/opt-in enrichments the backend leaves out of an empty request.
      await dataSourcesStore.init()
      results.value = (await Ip.Get(props.input[0], dataSourcesStore.queryableEnrichmentSlugs)).data
      // Ensure IP address is in results for IpView to use
      if (!results.value.ip) {
        results.value.ip = props.input[0]
      }
    } else if (props.type === 'ip_table') {
      // /query/ip/bulk rows are { ip, enrichments }; IpResultsTable now keys on
      // the uniform `selector` field, so alias it here.
      results.value = (await Ip.bulk(props.input)).data.map(r => ({ ...r, selector: r.ip }))
    } else {
      throw new Error('Invalid query type: ' + props.type)
    }

    if (props.log) {
      User.save_query(props.input, props.type)
    }
  } catch(e) {
    console.error('QueryRunner error:', e);
    failed.value = true

    // Extract a user-friendly error message
    if (e.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (e.response.status === 400 && e.response.data?.message) {
        error_message.value = e.response.data.message
      } else if (e.response.status === 400) {
        error_message.value = "Bad request. Please check your input and try again."
      } else if (e.response.status === 429) {
        error_message.value = "Too many requests. Please wait a moment and try again."
      } else if (e.response.status === 401) {
        error_message.value = "Unauthorized. Please log in and try again."
      } else if (e.response.data?.message) {
        error_message.value = e.response.data.message
      } else {
        error_message.value = `Server error (${e.response.status}). Please try again later.`
      }
    } else if (e.request) {
      // The request was made but no response was received
      error_message.value = "No response from server. Please check your connection."
    } else {
      // Something happened in setting up the request that triggered an Error
      error_message.value = e.message || "An unexpected error occurred."
    }
  }
  running.value = false
  console.log('QueryRunner finished - running:', running.value, 'results:', results.value, 'failed:', failed.value);
})

</script>

<template>
  <div v-if="!failed" class="runner-container">
    <div v-if="!running" class="results-container">
      <IpView v-if="props.type === 'ip' && results != null" :data="results"/>
      <IpTableView v-else-if="props.type === 'ip_table' && results != null" :data="results" />
    </div>
    <div v-else class="loading-container">
      <h2 class="mb-3 jura">Loading</h2>
      <div class="icon">
        <font-awesome-icon class="ft" style="font-size: 4rem; color: var(--text-secondary)" :icon="['fas', 'crosshairs']" beat-fade />
      </div>
    </div>
  </div>
  <div v-else class="error-container">
    <div class="error-content">
      <font-awesome-icon style="font-size: 7rem; color: var(--color-error);" class="mb-4" :icon="['fas', 'circle-exclamation']"/>
      <div>
        <h4 class="text-center">Query failed</h4>
        <p class="text-center">
            {{ error_message }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.runner-container {
  height: 100%;
  width: 100%;
  display: flex;
}

.results-container {
  display: flex;
  min-height: 0;
  width: 100%;
  flex: 1;
  flex-direction: column;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.error-container {
  width: 100%;
}

.error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 3rem;
  height: 100%;
  width: 100%;
}
</style>

